import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container } from '@/components/ui';
import './ReportingView.css';
import { areasService, ServiceArea } from '../../../services/areasService';
import { usePermissions } from '../../../hooks/usePermissions';
import ReportSidebar from '../components/ReportSidebar';
import D3ReportView from './D3ReportView';
import D2ReportView from './D2ReportView';
import ReportHistoryView from './ReportHistoryView';
import WildlifeReportView from './WildlifeReportView';
import RockfallReportView from './RockfallReportView';
import SignsReportView from './SignsReportView';
import CulvertsReportView from './CulvertsReportView';
import CattleguardReportView from './CattleguardReportView';
import EquipmentUsageReportView from './EquipmentUsageReportView';
import MaterialUsageReportView from './MaterialUsageReportView';
import PreliminaryJobCostReportView from './PreliminaryJobCostReportView';
import QBSSummaryReportView from './QBSSummaryReportView';
import QBSActualDetailsReportView from './QBSActualDetailsReportView';
import QBSQuantifiedValueReportView from './QBSQuantifiedValueReportView';
import { LEGACY_QUANTIFIED_VALUE_REPORT_SECTION, QUANTIFIED_VALUE_REPORT_SECTION } from '../utils/quantifiedValueReport';
import QBSProjectSummaryView from './QBSProjectSummaryView';
import OverdueTasksReportView from './OverdueTasksReportView';
import WeeklyPayrollReportView from './WeeklyPayrollReportView';
import WeeklyOvertimeReportView from './WeeklyOvertimeReportView';
import PayrollVarianceReportView from './PayrollVarianceReportView';
import OperationsLabourReportView from './OperationsLabourReportView';
import FinanceMonthEndReportView from './FinanceMonthEndReportView';
import EquipmentOpenDefectsReportView, { filtersFromSearchParams } from './EquipmentOpenDefectsReportView';
import ComingSoonView from '../components/ComingSoonView';
import DefaultReportsView from '../components/DefaultReportsView';
import VendorComplianceReportView from './VendorComplianceReportView';
import { VENDOR_COMPLIANCE_PERMISSIONS, VENDOR_COMPLIANCE_SECTION } from '../vendorCompliance';

const OPEN_DEFECTS_SECTION = 'equipment-open-defects';

const ReportingView: React.FC = () => {
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [loadingServiceAreas, setLoadingServiceAreas] = useState(true);
  const { hasPermission } = usePermissions();
  const canCreateD2 = hasPermission('report:d2_create');
  const canCreateD3 = hasPermission('report:d3_create');
  const canCreateWildlife = hasPermission('report:wildlife_create');
  const canCreateRockfall = hasPermission('report:rockfall_create');
  const canCreateSigns = hasPermission('report:signs_create');
  const canCreateCulverts = hasPermission('report:culverts_create');
  const canCreateCattleguard = hasPermission('report:cattleguard_create');
  const canCreateEquipmentUsage = hasPermission('report:equipment_usage_create');
  const canCreateMaterialUsage = hasPermission('report:material_usage_create');
  const canCreateOverdueTasks = hasPermission('report:overdue_tasks_create');
  const canCreateLabourSubmittedTime = hasPermission('report:labour_submitted_time_create');
  const canCreateQBS = hasPermission('report:qbs_create');
  const canCreateWeeklyPayroll = hasPermission('report:payroll_weekly_create');
  const canCreatePayrollVariance = hasPermission('report:payroll_variance_create');
  const canViewFinanceMonthEnd = hasPermission('report:finance_monthend_create');
  const canViewReports = hasPermission('report:view');
  // EQ-30: Equipment > Open Defects is for View Repairs (the same key as the API).
  const canViewRepairs = hasPermission('equipment:repairs_view');
  const canViewVendorCompliance = VENDOR_COMPLIANCE_PERMISSIONS.some((key) => hasPermission(key));
  
  // Check if user is Material Controller only (has material_usage_create but not report:view)
  const isMaterialControllerOnly = canCreateMaterialUsage && !canViewReports;
  
  // Active section state
  const [activeSection, setActiveSection] = useState('');
  
  // Resizable sidebar state
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    // Load from localStorage or use default
    const saved = localStorage.getItem('reporting-sidebar-width');
    return saved ? parseInt(saved, 10) : 250; // Default 250px
  });
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchServiceAreas = async () => {
      try {
        setLoadingServiceAreas(true);
        const areas = await areasService.getServiceAreas();
        setServiceAreas(Array.isArray(areas) ? areas : []);
      } catch (error) {
        setServiceAreas([]);
      } finally {
        setLoadingServiceAreas(false);
      }
    };
    fetchServiceAreas();
  }, []);

  // Reopen the Quantified Value report from its URL params (written on Run Report) so the
  // browser Back button returns there after drilling into a task. Leaving that report drops them.
  const [searchParams, setSearchParams] = useSearchParams();
  const reportParam = searchParams.get('report');
  useEffect(() => {
    if (reportParam === LEGACY_QUANTIFIED_VALUE_REPORT_SECTION) {
      const next = new URLSearchParams(searchParams);
      next.set('report', QUANTIFIED_VALUE_REPORT_SECTION);
      setSearchParams(next, { replace: true });
    }
  }, [reportParam, searchParams, setSearchParams]);
  useEffect(() => {
    if (reportParam === QUANTIFIED_VALUE_REPORT_SECTION && canCreateQBS && !activeSection) {
      setActiveSection(QUANTIFIED_VALUE_REPORT_SECTION);
    }
    // V2-078: a Home compliance tile opens the report filtered to its status.
    if (reportParam === VENDOR_COMPLIANCE_SECTION && canViewVendorCompliance && !activeSection) {
      setActiveSection(VENDOR_COMPLIANCE_SECTION);
    }
  }, [reportParam, canCreateQBS, canViewVendorCompliance, activeSection]);
  const tileStatus = searchParams.get('status');
  const initialComplianceStatus = (['OVERDUE', 'DUE_SOON', 'MISSING', 'CURRENT'] as const).find(
    (s) => s === tileStatus,
  );
  // EQ-30: the Home Critical Defects widget links here as
  // ?report=equipment-open-defects&severity=Critical&serviceAreaId=16. Open that report with the
  // URL's filters; they stay in the URL until another report is picked (effect below).
  useEffect(() => {
    if (reportParam === OPEN_DEFECTS_SECTION && canViewRepairs && !activeSection) {
      setActiveSection(OPEN_DEFECTS_SECTION);
    }
  }, [reportParam, canViewRepairs, activeSection]);
  const openDefectsUrlFilters =
    reportParam === OPEN_DEFECTS_SECTION ? filtersFromSearchParams(searchParams) : undefined;
  useEffect(() => {
    if (activeSection && reportParam && activeSection !== reportParam) {
      setSearchParams({}, { replace: true });
    }
  }, [activeSection, reportParam, setSearchParams]);

  // Auto-select Material Usage Report for Material Controller role – unless the URL opens
  // Vendor Compliance (V3-006: a Home tile link used to fall back to Material Usage here).
  const vendorComplianceDeepLink = reportParam === VENDOR_COMPLIANCE_SECTION && canViewVendorCompliance;
  useEffect(() => {
    if (isMaterialControllerOnly && !activeSection && !vendorComplianceDeepLink) {
      setActiveSection('operations-material-usage');
    }
  }, [isMaterialControllerOnly, activeSection, vendorComplianceDeepLink]);

  // Get the first available service area ID for defaulting
  const defaultServiceAreaId = serviceAreas.length > 0 ? serviceAreas[0].id.toString() : '';

  // Handle mouse down on resize handle
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      const newWidth = e.clientX;
      // Constrain sidebar width between 150px and 500px
      const constrainedWidth = Math.max(150, Math.min(500, newWidth));
      setSidebarWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Save sidebar width to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('reporting-sidebar-width', sidebarWidth.toString());
  }, [sidebarWidth]);

  const renderMainContent = () => {
    // Show default view when no report is selected or when only a top-level category is selected
    if (
      !activeSection ||
      activeSection === 'mott' ||
      activeSection === 'finance' ||
      activeSection === 'payroll' ||
      activeSection === 'operations' ||
      activeSection === 'equipment' ||
      activeSection === 'procurement'
    ) {
      return <DefaultReportsView />;
    }
    
    if (activeSection === 'export-history') {
      return <ReportHistoryView canCreateD2={canCreateD2} canCreateD3={canCreateD3} />;
    }
    if (activeSection === 'mott-d3' || activeSection === 'd3') {
      return (
        <D3ReportView
          serviceAreas={serviceAreas}
          canCreateD3={canCreateD3}
          loadingServiceAreas={loadingServiceAreas}
          defaultServiceAreaId={defaultServiceAreaId}
        />
      );
    }
    if (activeSection === 'mott-d2' || activeSection === 'd2') {
      return (
        <D2ReportView
          serviceAreas={serviceAreas}
          canCreateD2={canCreateD2}
          loadingServiceAreas={loadingServiceAreas}
          defaultServiceAreaId={defaultServiceAreaId}
        />
      );
    }
    // Coming soon for all other sections
    switch (activeSection) {
      case VENDOR_COMPLIANCE_SECTION:
        return canViewVendorCompliance ? (
          <VendorComplianceReportView serviceAreas={serviceAreas} initialStatus={initialComplianceStatus ?? null} />
        ) : (
          <DefaultReportsView />
        );
      case 'mott-chris-signs':
        return (
          <SignsReportView
            serviceAreas={serviceAreas}
            canCreateSigns={canCreateSigns}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'mott-chris-culverts':
        return (
          <CulvertsReportView
            serviceAreas={serviceAreas}
            canCreateCulverts={canCreateCulverts}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'mott-chris-cattleguards':
        return (
          <CattleguardReportView
            serviceAreas={serviceAreas}
            canCreateCattleguard={canCreateCattleguard}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'mott-wildlife':
        return (
          <WildlifeReportView
            serviceAreas={serviceAreas}
            canCreateWildlife={canCreateWildlife}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'mott-rockfall':
        return (
          <RockfallReportView
            serviceAreas={serviceAreas}
            canCreateRockfall={canCreateRockfall}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'finance-monthend':
        return (
          <FinanceMonthEndReportView
            serviceAreas={serviceAreas}
            canViewFinanceMonthEnd={canViewFinanceMonthEnd}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'operations-equipment-usage':
        return (
          <EquipmentUsageReportView
            serviceAreas={serviceAreas}
            canCreateEquipmentUsage={canCreateEquipmentUsage}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'finance-preliminary-job-cost':
        return (
          <PreliminaryJobCostReportView
            serviceAreas={serviceAreas}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'operations-material-usage':
        return (
          <MaterialUsageReportView
            serviceAreas={serviceAreas}
            canCreateMaterialUsage={canCreateMaterialUsage}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'operations':
        return <ComingSoonView title="Operations Reports" />;
      case 'operations-overdue-tasks':
        return (
          <OverdueTasksReportView
            serviceAreas={serviceAreas}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'operations-labour':
        return (
          <OperationsLabourReportView
            serviceAreas={serviceAreas}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'operations-qbs-summary':
        return (
          <QBSSummaryReportView
            serviceAreas={serviceAreas}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'operations-qbs-project-summary':
        return (
          <QBSProjectSummaryView
            serviceAreas={serviceAreas}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'operations-quantified-costing':
        return (
          <QBSActualDetailsReportView
            serviceAreas={serviceAreas}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'operations-quantified-value':
        return (
          <QBSQuantifiedValueReportView
            serviceAreas={serviceAreas}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'payroll-weekly':
        return (
          <WeeklyPayrollReportView
            serviceAreas={serviceAreas}
            canCreateWeeklyPayroll={canCreateWeeklyPayroll}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'payroll-weekly-overtime':
        return (
          <WeeklyOvertimeReportView
            serviceAreas={serviceAreas}
            canCreateWeeklyPayroll={canCreateWeeklyPayroll}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case 'payroll-variance':
        return (
          <PayrollVarianceReportView
            serviceAreas={serviceAreas}
            canCreatePayrollVariance={canCreatePayrollVariance}
            loadingServiceAreas={loadingServiceAreas}
            defaultServiceAreaId={defaultServiceAreaId}
          />
        );
      case OPEN_DEFECTS_SECTION:
        if (!canViewRepairs) return <ComingSoonView />;
        return (
          <EquipmentOpenDefectsReportView
            // A new link (a different widget tile) re-opens the report with its filters.
            key={openDefectsUrlFilters ? searchParams.toString() : 'open-defects'}
            serviceAreas={serviceAreas}
            loadingServiceAreas={loadingServiceAreas}
            initialFilters={openDefectsUrlFilters}
          />
        );
      default:
        return <ComingSoonView />;
    }
  };

  return (
    <Container fluid className="tw-p-0 page-content-with-navbar-offset tw-h-full">
      {/*
        Below md: stack the sidebar above the content instead of splitting the row - a fixed
        pixel-width sidebar next to a report has no room to give on a phone. ReportSidebar
        itself collapses to a compact <select> at that width (see ReportSidebar.tsx), so this
        row only needs to stop being a *row*, and let the resize handle - meaningless with a
        vertical stack - disappear.
        h-full instead of a 95vh magic number: the app shell (wrap-app-shell) is already the
        bounded viewport-height flex column, so this just needs to fill the pane it's given.
      */}
      <div className="tw-flex tw-h-full tw-flex-col tw-overflow-hidden md:tw-flex-row">
        {/* Sidebar */}
        <div
          ref={sidebarRef}
          className="tw-max-h-[45vh] tw-flex-shrink-0 tw-overflow-hidden tw-border-b tw-border-line md:tw-max-h-none md:tw-border-b-0 md:tw-border-r"
          style={{ backgroundColor: '#f8f9fa' }}
        >
          {/* The resizable pixel width only applies at md+ (see ReportingView.css); below that
              the sidebar is full width and renders its own <select> report picker instead. */}
          <div
            className="reporting-sidebar-panel tw-h-full"
            style={{ '--reporting-sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}
          >
            <ReportSidebar
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              canCreateD2={canCreateD2}
              canCreateD3={canCreateD3}
              canCreateWildlife={canCreateWildlife}
              canCreateRockfall={canCreateRockfall}
              canCreateSigns={canCreateSigns}
              canCreateCulverts={canCreateCulverts}
              canCreateCattleguard={canCreateCattleguard}
              canCreateEquipmentUsage={canCreateEquipmentUsage}
              canCreateMaterialUsage={canCreateMaterialUsage}
              canCreateOverdueTasks={canCreateOverdueTasks}
              canCreateLabourSubmittedTime={canCreateLabourSubmittedTime}
              canCreateQBS={canCreateQBS}
              canCreateWeeklyPayroll={canCreateWeeklyPayroll}
              canCreatePayrollVariance={canCreatePayrollVariance}
              canViewFinanceMonthEnd={canViewFinanceMonthEnd}
              canViewReports={canViewReports}
              canViewRepairs={canViewRepairs}
              canViewVendorCompliance={canViewVendorCompliance}
              isMaterialControllerOnly={isMaterialControllerOnly}
            />
          </div>
        </div>

        {/* Resize Handle - desktop/tablet only, dragging a width makes no sense once the
            sidebar is a full-width stacked block. */}
        <div
          ref={resizeHandleRef}
          onMouseDown={handleMouseDown}
          className="tw-hidden md:tw-block"
          style={{
            width: '4px',
            cursor: 'col-resize',
            backgroundColor: isResizing ? '#007bff' : '#dee2e6',
            flexShrink: 0,
            position: 'relative',
            zIndex: 10,
            transition: isResizing ? 'none' : 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            if (!isResizing) {
              e.currentTarget.style.backgroundColor = '#adb5bd';
            }
          }}
          onMouseLeave={(e) => {
            if (!isResizing) {
              e.currentTarget.style.backgroundColor = '#dee2e6';
            }
          }}
        />

        {/* Main Content */}
        <div
          style={{
            flex: 1,
            overflow: 'hidden',
            minWidth: 0, // Allows flex item to shrink below content size
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div
            className="reporting-main-pane tw-p-2 sm:tw-p-4"
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            {renderMainContent()}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default ReportingView; 